import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { useDateFormat } from '../contexts/DateFormatContext';
import { useOddsFormat } from '../contexts/OddsFormatContext';
import DateInput from '../components/DateInput';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Layers, X } from 'lucide-react';
import { trackEvent } from '../components/GoogleAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddBet = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { formatDate } = useDateFormat();
  const { getOddsPlaceholder, convertToDecimal } = useOddsFormat();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [betLimitInfo, setBetLimitInfo] = useState(null);
  const [bookmakers, setBookmakers] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [customMarkets, setCustomMarkets] = useState([]);
  const [customBookmakers, setCustomBookmakers] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [teamsCache, setTeamsCache] = useState({}); // Cache teams by league
  
  // Bet type: 'single' or 'accumulator'
  const [betType, setBetType] = useState('single');
  
  // Accumulator selections
  const [selections, setSelections] = useState([]);
  
  // Common fields for all bet types
  const [commonData, setCommonData] = useState({
    bookmaker: '',
    customBookmaker: '',
    tipster: '',
    strategy: '',
    stake: '',
    bet_date: new Date().toISOString().split('T')[0]
  });
  
  // Single bet form data
  const [formData, setFormData] = useState({
    league: '',
    home_team: '',
    away_team: '',
    market: '',
    customMarket: '',
    odds: ''
  });

  // Current selection being added (for accumulator)
  const [currentSelection, setCurrentSelection] = useState({
    league: '',
    home_team: '',
    away_team: '',
    market: '',
    customMarket: '',
    odds: ''
  });
  const [currentTeams, setCurrentTeams] = useState([]);

  useEffect(() => {
    fetchData();
    fetchBetLimitInfo();
  }, []);

  useEffect(() => {
    if (formData.league && betType === 'single') {
      fetchTeamsForLeague(formData.league);
    }
  }, [formData.league, betType]);

  const fetchBetLimitInfo = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/bets/limit-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBetLimitInfo(response.data);
    } catch (error) {
      console.error('Error fetching bet limit info:', error);
    }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('session_token');
    try {
      const [bookmakersRes, marketsRes, leaguesRes, customMarketsRes, customBookmakersRes] = await Promise.all([
        axios.get(`${API}/data/bookmakers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/data/markets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/data/leagues?popular_only=true&limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/data/my-custom-markets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/data/my-custom-bookmakers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      setBookmakers(bookmakersRes.data.bookmakers);
      setMarkets(marketsRes.data.markets);
      setLeagues(leaguesRes.data.leagues);
      setCustomMarkets(customMarketsRes.data.markets || []);
      setCustomBookmakers(customBookmakersRes.data.bookmakers || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load form data');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchTeamsForLeague = async (league, forSelection = false) => {
    // Check cache first
    if (teamsCache[league]) {
      if (forSelection) {
        setCurrentTeams(teamsCache[league]);
      }
      return teamsCache[league];
    }
    
    const token = localStorage.getItem('session_token');
    try {
      const response = await axios.get(`${API}/data/teams/${encodeURIComponent(league)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const teams = response.data.teams;
      setTeamsCache(prev => ({ ...prev, [league]: teams }));
      if (forSelection) {
        setCurrentTeams(teams);
      }
      return teams;
    } catch (error) {
      console.error('Error fetching teams:', error);
      toast.error('Failed to load teams');
      return [];
    }
  };

  // Calculate total odds for accumulator
  const calculateTotalOdds = () => {
    if (selections.length === 0) return 0;
    return selections.reduce((total, sel) => total * sel.odds, 1);
  };

  // Add selection to accumulator
  const addSelection = () => {
    const decimalOdds = convertToDecimal(currentSelection.odds);
    if (!decimalOdds || decimalOdds < 1) {
      toast.error('Please enter valid odds');
      return;
    }
    
    if (!currentSelection.league || !currentSelection.home_team || !currentSelection.away_team || !currentSelection.market) {
      toast.error('Please fill all selection fields');
      return;
    }

    const finalMarket = currentSelection.market === 'Custom' ? currentSelection.customMarket : currentSelection.market;
    
    const newSelection = {
      id: Date.now().toString(),
      league: currentSelection.league,
      home_team: currentSelection.home_team,
      away_team: currentSelection.away_team,
      market: finalMarket,
      odds: decimalOdds
    };
    
    setSelections([...selections, newSelection]);
    
    // Reset current selection
    setCurrentSelection({
      league: '',
      home_team: '',
      away_team: '',
      market: '',
      customMarket: '',
      odds: ''
    });
    setCurrentTeams([]);
    
    toast.success('Selection added!');
  };

  // Remove selection from accumulator
  const removeSelection = (id) => {
    setSelections(selections.filter(s => s.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const token = localStorage.getItem('session_token');
    
    // Handle custom bookmaker
    let finalBookmaker = commonData.bookmaker === 'Custom' ? commonData.customBookmaker : commonData.bookmaker;
    
    if (commonData.bookmaker === 'Custom' && commonData.customBookmaker) {
      try {
        await axios.post(`${API}/data/add-custom-bookmaker?bookmaker=${encodeURIComponent(commonData.customBookmaker)}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomBookmakers([...customBookmakers, commonData.customBookmaker]);
      } catch (error) {
        console.error('Error saving custom bookmaker:', error);
      }
    }
    
    try {
      if (betType === 'single') {
        // Single bet submission
        const decimalOdds = convertToDecimal(formData.odds);
        if (!decimalOdds || decimalOdds < 1) {
          toast.error('Please enter valid odds');
          setLoading(false);
          return;
        }

        // Handle custom market
        let finalMarket = formData.market === 'Custom' ? formData.customMarket : formData.market;
        
        if (formData.market === 'Custom' && formData.customMarket) {
          try {
            await axios.post(`${API}/data/add-custom-market?market=${encodeURIComponent(formData.customMarket)}`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setCustomMarkets([...customMarkets, formData.customMarket]);
          } catch (error) {
            console.error('Error saving custom market:', error);
          }
        }

        await axios.post(`${API}/bets`, {
          bet_type: 'single',
          league: formData.league,
          home_team: formData.home_team,
          away_team: formData.away_team,
          bookmaker: finalBookmaker,
          market: finalMarket,
          tipster: commonData.tipster,
          strategy: commonData.strategy,
          odds: decimalOdds,
          stake: parseFloat(commonData.stake),
          bet_date: new Date(commonData.bet_date).toISOString()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Accumulator bet submission
        if (selections.length < 2) {
          toast.error('Accumulator must have at least 2 selections');
          setLoading(false);
          return;
        }

        const totalOdds = calculateTotalOdds();
        
        await axios.post(`${API}/bets`, {
          bet_type: 'accumulator',
          league: selections[0].league,
          home_team: selections[0].home_team,
          away_team: selections[0].away_team,
          bookmaker: finalBookmaker,
          market: `Accumulator (${selections.length} selections)`,
          tipster: commonData.tipster,
          strategy: commonData.strategy,
          odds: totalOdds,
          stake: parseFloat(commonData.stake),
          bet_date: new Date(commonData.bet_date).toISOString(),
          selections: selections.map(s => ({
            league: s.league,
            home_team: s.home_team,
            away_team: s.away_team,
            market: s.market,
            odds: s.odds
          }))
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success('Bet added successfully!');
      
      // Track bet creation event
      trackEvent('bet_created', 'Betting', betType, betType === 'accumulator' ? selections.length : 1);
      
      fetchBetLimitInfo();
      navigate('/bets');
    } catch (error) {
      console.error('Error adding bet:', error);
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || 'Monthly bet limit reached. Upgrade to Premium for unlimited bets!');
      } else {
        toast.error('Failed to add bet');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCommonChange = (e) => {
    setCommonData({ ...commonData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    if (name === 'league') {
      setFormData(prev => ({ ...prev, [name]: value, home_team: '', away_team: '' }));
      if (value) {
        fetchTeamsForLeague(value);
      }
    }
  };

  const handleCommonSelectChange = (name, value) => {
    setCommonData({ ...commonData, [name]: value });
  };

  const handleSelectionChange = (name, value) => {
    setCurrentSelection({ ...currentSelection, [name]: value });
    if (name === 'league') {
      setCurrentSelection(prev => ({ ...prev, [name]: value, home_team: '', away_team: '' }));
      if (value) {
        fetchTeamsForLeague(value, true);
      } else {
        setCurrentTeams([]);
      }
    }
  };

  if (dataLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-white text-lg">Loading form data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const totalOdds = calculateTotalOdds();
  const potentialReturn = totalOdds * (parseFloat(commonData.stake) || 0);
  const singlePotentialReturn = (convertToDecimal(formData.odds) || 0) * (parseFloat(commonData.stake) || 0);

  return (
    <Layout>
      <div data-testid="add-bet-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white">Add New Bet</h1>
            {betLimitInfo && !betLimitInfo.unlimited && (
              <div className="glass p-4 rounded-lg">
                <div className="text-sm text-gray-400">Monthly Bets</div>
                <div className="text-2xl font-bold text-white">
                  {betLimitInfo.remaining}/{betLimitInfo.monthly_limit}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Resets: {formatDate(betLimitInfo.reset_date)}
                </div>
              </div>
            )}
          </div>

          {/* Bet Type Selector */}
          <div className="flex gap-4 mb-6">
            <button
              type="button"
              onClick={() => setBetType('single')}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                betType === 'single'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                  : 'glass text-gray-400 hover:text-white'
              }`}
            >
              Single Bet
            </button>
            <button
              type="button"
              onClick={() => setBetType('accumulator')}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                betType === 'accumulator'
                  ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white'
                  : 'glass text-gray-400 hover:text-white'
              }`}
            >
              <Layers className="w-5 h-5" />
              Accumulator
            </button>
          </div>

          <form data-testid="add-bet-form" onSubmit={handleSubmit}>
            {/* ACCUMULATOR MODE */}
            {betType === 'accumulator' && (
              <>
                {/* Current Selections */}
                {selections.length > 0 && (
                  <Card className="glass-dark border-white/10 mb-6">
                    <CardContent className="pt-6">
                      <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                        <Layers className="w-5 h-5 text-purple-400" />
                        Selections ({selections.length})
                      </h3>
                      <div className="space-y-3">
                        {selections.map((sel, index) => (
                          <div key={sel.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10">
                            <div className="flex-1">
                              <div className="text-white font-medium">
                                {index + 1}. {sel.home_team} vs {sel.away_team}
                              </div>
                              <div className="text-gray-400 text-sm">
                                {sel.league} • {sel.market}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-green-400 font-bold">{sel.odds.toFixed(2)}</span>
                              <button
                                type="button"
                                onClick={() => removeSelection(sel.id)}
                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                        <span className="text-gray-400">Combined Odds:</span>
                        <span className="text-2xl font-bold text-green-400">{totalOdds.toFixed(2)}</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Add New Selection */}
                <Card className="glass-dark border-white/10 mb-6">
                  <CardContent className="pt-6">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Plus className="w-5 h-5 text-blue-400" />
                      Add Selection
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white">League *</Label>
                        <Select value={currentSelection.league} onValueChange={(value) => handleSelectionChange('league', value)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                            <SelectValue placeholder="Select league" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {leagues.map((league) => (
                              <SelectItem key={league} value={league}>{league}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-white">Market *</Label>
                        <Select value={currentSelection.market} onValueChange={(value) => handleSelectionChange('market', value)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                            <SelectValue placeholder="Select market" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {customMarkets.map((market) => (
                              <SelectItem key={`custom-${market}`} value={market}>⭐ {market}</SelectItem>
                            ))}
                            {markets.map((market) => (
                              <SelectItem key={market} value={market}>{market}</SelectItem>
                            ))}
                            <SelectItem value="Custom">+ Add Custom Market</SelectItem>
                          </SelectContent>
                        </Select>
                        {currentSelection.market === 'Custom' && (
                          <Input
                            placeholder="Enter custom market"
                            value={currentSelection.customMarket}
                            onChange={(e) => setCurrentSelection({ ...currentSelection, customMarket: e.target.value })}
                            className="bg-white/5 border-white/10 text-white mt-2"
                          />
                        )}
                      </div>

                      <div>
                        <Label className="text-white">Home Team *</Label>
                        <Select 
                          value={currentSelection.home_team} 
                          onValueChange={(value) => setCurrentSelection({ ...currentSelection, home_team: value })}
                          disabled={!currentSelection.league}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                            <SelectValue placeholder={currentSelection.league ? "Select home team" : "Select league first"} />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {currentTeams.map((team) => (
                              <SelectItem key={team} value={team}>{team}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-white">Away Team *</Label>
                        <Select 
                          value={currentSelection.away_team} 
                          onValueChange={(value) => setCurrentSelection({ ...currentSelection, away_team: value })}
                          disabled={!currentSelection.league}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2">
                            <SelectValue placeholder={currentSelection.league ? "Select away team" : "Select league first"} />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {currentTeams.filter(t => t !== currentSelection.home_team).map((team) => (
                              <SelectItem key={team} value={team}>{team}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-white">Odds *</Label>
                        <Input
                          type="text"
                          placeholder={getOddsPlaceholder()}
                          value={currentSelection.odds}
                          onChange={(e) => setCurrentSelection({ ...currentSelection, odds: e.target.value })}
                          className="bg-white/5 border-white/10 text-white mt-2"
                        />
                      </div>

                      <div className="flex items-end">
                        <Button
                          type="button"
                          onClick={addSelection}
                          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add to Accumulator
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* SINGLE BET MODE */}
            {betType === 'single' && (
              <Card className="glass-dark border-white/10 mb-6">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="league" className="text-white">League *</Label>
                      <Select value={formData.league} onValueChange={(value) => handleSelectChange('league', value)}>
                        <SelectTrigger data-testid="league-select" className="bg-white/5 border-white/10 text-white mt-2">
                          <SelectValue placeholder="Select league" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {leagues.map((league) => (
                            <SelectItem key={league} value={league}>{league}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="market" className="text-white">Market *</Label>
                      <Select value={formData.market} onValueChange={(value) => handleSelectChange('market', value)}>
                        <SelectTrigger data-testid="market-select" className="bg-white/5 border-white/10 text-white mt-2">
                          <SelectValue placeholder="Select market" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {customMarkets.map((market) => (
                            <SelectItem key={`custom-${market}`} value={market}>⭐ {market}</SelectItem>
                          ))}
                          {markets.map((market) => (
                            <SelectItem key={market} value={market}>{market}</SelectItem>
                          ))}
                          <SelectItem value="Custom">+ Add Custom Market</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.market === 'Custom' && (
                        <Input
                          name="customMarket"
                          placeholder="Enter custom market"
                          value={formData.customMarket}
                          onChange={handleChange}
                          className="bg-white/5 border-white/10 text-white mt-2"
                        />
                      )}
                    </div>

                    <div>
                      <Label htmlFor="home_team" className="text-white">Home Team *</Label>
                      <Select 
                        value={formData.home_team} 
                        onValueChange={(value) => handleSelectChange('home_team', value)}
                        disabled={!formData.league}
                      >
                        <SelectTrigger data-testid="home-team-select" className="bg-white/5 border-white/10 text-white mt-2">
                          <SelectValue placeholder={formData.league ? "Select home team" : "Select league first"} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {(teamsCache[formData.league] || []).map((team) => (
                            <SelectItem key={team} value={team}>{team}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="away_team" className="text-white">Away Team *</Label>
                      <Select 
                        value={formData.away_team} 
                        onValueChange={(value) => handleSelectChange('away_team', value)}
                        disabled={!formData.league}
                      >
                        <SelectTrigger data-testid="away-team-select" className="bg-white/5 border-white/10 text-white mt-2">
                          <SelectValue placeholder={formData.league ? "Select away team" : "Select league first"} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {(teamsCache[formData.league] || []).filter(t => t !== formData.home_team).map((team) => (
                            <SelectItem key={team} value={team}>{team}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="odds" className="text-white">Odds *</Label>
                      <Input
                        data-testid="odds-input"
                        type="text"
                        name="odds"
                        placeholder={getOddsPlaceholder()}
                        value={formData.odds}
                        onChange={handleChange}
                        className="bg-white/5 border-white/10 text-white mt-2"
                        required
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* COMMON FIELDS */}
            <Card className="glass-dark border-white/10 mb-6">
              <CardContent className="pt-6">
                <h3 className="text-white font-semibold mb-4">Bet Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="bookmaker" className="text-white">Bookmaker *</Label>
                    <Select value={commonData.bookmaker} onValueChange={(value) => handleCommonSelectChange('bookmaker', value)}>
                      <SelectTrigger data-testid="bookmaker-select" className="bg-white/5 border-white/10 text-white mt-2">
                        <SelectValue placeholder="Select bookmaker" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {customBookmakers.map((bm) => (
                          <SelectItem key={`custom-${bm}`} value={bm}>⭐ {bm}</SelectItem>
                        ))}
                        {bookmakers.map((bm) => (
                          <SelectItem key={bm} value={bm}>{bm}</SelectItem>
                        ))}
                        <SelectItem value="Custom">+ Add Custom Bookmaker</SelectItem>
                      </SelectContent>
                    </Select>
                    {commonData.bookmaker === 'Custom' && (
                      <Input
                        name="customBookmaker"
                        placeholder="Enter custom bookmaker"
                        value={commonData.customBookmaker}
                        onChange={handleCommonChange}
                        className="bg-white/5 border-white/10 text-white mt-2"
                      />
                    )}
                  </div>

                  <div>
                    <Label htmlFor="stake" className="text-white">Stake *</Label>
                    <Input
                      data-testid="stake-input"
                      type="number"
                      name="stake"
                      placeholder="Enter stake amount"
                      value={commonData.stake}
                      onChange={handleCommonChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <Label htmlFor="tipster" className="text-white">Tipster (Optional)</Label>
                    <Input
                      data-testid="tipster-input"
                      type="text"
                      name="tipster"
                      placeholder="Who gave you this tip?"
                      value={commonData.tipster}
                      onChange={handleCommonChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="strategy" className="text-white">Strategy (Optional)</Label>
                    <Input
                      data-testid="strategy-input"
                      type="text"
                      name="strategy"
                      placeholder="Which strategy are you using?"
                      value={commonData.strategy}
                      onChange={handleCommonChange}
                      className="bg-white/5 border-white/10 text-white mt-2"
                    />
                  </div>

                  <div>
                    <DateInput
                      label="Bet Date *"
                      value={commonData.bet_date}
                      onChange={(e) => setCommonData({ ...commonData, bet_date: e.target.value })}
                      required
                    />
                  </div>

                  {/* Potential Return Display */}
                  <div className="flex items-end">
                    <div className="w-full p-4 rounded-lg bg-gradient-to-r from-green-500/10 to-green-700/10 border border-green-500/20">
                      <div className="text-sm text-gray-400">Potential Return</div>
                      <div className="text-2xl font-bold text-green-400">
                        {betType === 'single' 
                          ? singlePotentialReturn.toFixed(2)
                          : potentialReturn.toFixed(2)
                        }
                      </div>
                      {betType === 'accumulator' && selections.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {selections.length} selections @ {totalOdds.toFixed(2)} odds
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || (betType === 'accumulator' && selections.length < 2)}
              className={`w-full py-6 text-lg font-semibold ${
                betType === 'accumulator'
                  ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Adding Bet...
                </span>
              ) : betType === 'accumulator' ? (
                `Add Accumulator (${selections.length} selections)`
              ) : (
                'Add Single Bet'
              )}
            </Button>
            
            {betType === 'accumulator' && selections.length < 2 && (
              <p className="text-center text-yellow-400 text-sm mt-2">
                Add at least 2 selections to create an accumulator
              </p>
            )}
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default AddBet;
